"use client";

import React from "react";
import { Radio as AntRadio, ConfigProvider } from "antd";
import type { RadioProps as AntRadioProps } from "antd";
import { orionTheme } from "../theme/orion-theme";
import clsx from "clsx";
import "../styles/radio.css";

export interface RadioProps extends AntRadioProps {
  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;
}

/**
 * Radio component - Ant Design Radio with OpenStore Orion theming
 *
 * This component provides FULL access to all Ant Design Radio props while
 * automatically applying the OpenStore Orion theme and Figma design specifications.
 *
 * Features:
 * - Figma design specifications (16x16px, specific colors, inner circle styling)
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design Radio features and props
 *
 * @example
 * ```tsx
 * // Basic radio with Orion theming
 * <Radio>Option 1</Radio>
 *
 * // All Ant Design Radio props work
 * <Radio
 *   value="option1"
 *   checked={isSelected}
 *   onChange={handleChange}
 *   disabled={isDisabled}
 *   autoFocus
 *   className="custom-radio"
 * />
 *
 * // Radio group
 * <Radio.Group
 *   options={[
 *     { label: 'Option 1', value: '1' },
 *     { label: 'Option 2', value: '2' },
 *     { label: 'Option 3', value: '3' }
 *   ]}
 *   value={selectedValue}
 *   onChange={handleGroupChange}
 * />
 * ```
 */
export const Radio: React.FC<RadioProps> = ({ className, ...props }) => {
  return (
    <ConfigProvider theme={orionTheme}>
      <AntRadio className={clsx("orion-radio", className)} {...props} />
    </ConfigProvider>
  );
};

Radio.displayName = "Radio";

// Export convenience components
export const RadioGroup = AntRadio.Group;
export const RadioButton = AntRadio.Button;
